import { useState, useEffect } from 'react'
import { useParams } from 'react-router-dom'
import toast from 'react-hot-toast'
import { AlternativesVoting } from '../components/sections/alternatives-voting'
import type { VoteType, AlternativeProposalFormData, UserVotes, Alternative } from '../types/alternatives-voting'
import { supabase } from '../lib/supabase'
import { useAuth } from '../contexts/AuthContext'

export function Alternatives() {
  const { id } = useParams<{ id: string }>()
  const { user } = useAuth()

  const [loading, setLoading] = useState(true)
  const [foreignWord, setForeignWord] = useState<any>(null)
  const [userVotes, setUserVotes] = useState<UserVotes>({})
  const [users, setUsers] = useState<any[]>([])

  // Fetch word, alternatives, and user votes from Supabase
  useEffect(() => {
    if (!id) return

    async function fetchData() {
      setLoading(true)
      try {
        // Fetch the foreign word with all related data
        const { data: wordData, error: wordError } = await supabase
          .from('foreign_words')
          .select(`
            *,
            category:categories(*),
            submitter:profiles!foreign_words_submitted_by_fkey(*),
            alternatives(
              *,
              proposer:profiles!alternatives_proposed_by_fkey(*)
            )
          `)
          .eq('id', id)
          .single()

        if (wordError) throw wordError
        if (!wordData) throw new Error('Word not found')

        setForeignWord(wordData)

        // Fetch user's votes if logged in
        if (user && wordData.alternatives) {
          const alternativeIds = wordData.alternatives.map((alt: any) => alt.id)

          if (alternativeIds.length > 0) {
            const { data: votesData } = await supabase
              .from('votes')
              .select('alternative_id, vote_type')
              .eq('user_id', user.id)
              .in('alternative_id', alternativeIds)

            const votesMap = votesData?.reduce((acc, vote) => {
              acc[vote.alternative_id] = vote.vote_type
              return acc
            }, {} as UserVotes) || {}

            setUserVotes(votesMap)
          }
        }

        // Extract unique users for profiles
        const allUsers = [
          wordData.submitter,
          ...(wordData.alternatives || []).map((alt: any) => alt.proposer)
        ].filter(Boolean)

        const uniqueUsers = Array.from(
          new Map(allUsers.map(u => [u.id, u])).values()
        )

        setUsers(uniqueUsers)
      } catch (error) {
        console.error('Error fetching word:', error)
      } finally {
        setLoading(false)
      }
    }

    fetchData()
  }, [id, user])

  const handleVote = async (alternativeId: string, voteType: VoteType) => {
    if (!user) {
      toast.error('يرجى تسجيل الدخول للتصويت')
      return
    }

    try {
      // Upsert vote in database
      const { error } = await supabase
        .from('votes')
        .upsert({
          alternative_id: alternativeId,
          user_id: user.id,
          vote_type: voteType,
        }, {
          onConflict: 'alternative_id,user_id'
        })

      if (error) throw error

      // Update local state optimistically
      setUserVotes(prev => ({
        ...prev,
        [alternativeId]: voteType,
      }))

      // Refetch the word to get updated vote counts
      const { data: wordData } = await supabase
        .from('foreign_words')
        .select(`
          *,
          category:categories(*),
          submitter:profiles!foreign_words_submitted_by_fkey(*),
          alternatives(
            *,
            proposer:profiles!alternatives_proposed_by_fkey(*)
          )
        `)
        .eq('id', id)
        .single()

      if (wordData) {
        setForeignWord(wordData)
      }
    } catch (error) {
      console.error('Error voting:', error)
      toast.error('حدث خطأ أثناء التصويت')
    }
  }

  const handleShare = () => {
    if (!mappedForeignWord) return

    const url = window.location.href

    if (navigator.share) {
      navigator
        .share({
          title: `${mappedForeignWord.wordArabic}`,
          text: '',
          url: url,
        })
        .catch((err) => console.log('Error sharing:', err))
    } else {
      navigator.clipboard.writeText(url)
      toast.success('تم نسخ الرابط!')
    }
  }

  const handleProposeAlternative = async (formData: AlternativeProposalFormData) => {
    if (!user) {
      toast.error('يرجى تسجيل الدخول لاقتراح بديل')
      return
    }

    if (!id) return

    try {
      // Insert new alternative into database
      const { error } = await supabase
        .from('alternatives')
        .insert({
          foreign_word_id: id,
          egyptian_word: formData.egyptianWord,
          explanation: formData.explanation,
          proposed_by: user.id,
        })

      if (error) throw error

      toast.success('تم إضافة البديل بنجاح!')

      // Refetch the word to get the new alternative
      const { data: wordData } = await supabase
        .from('foreign_words')
        .select(`
          *,
          category:categories(*),
          submitter:profiles!foreign_words_submitted_by_fkey(*),
          alternatives(
            *,
            proposer:profiles!alternatives_proposed_by_fkey(*)
          )
        `)
        .eq('id', id)
        .single()

      if (wordData) {
        setForeignWord(wordData)
      }
    } catch (error) {
      console.error('Error proposing alternative:', error)
      toast.error('حدث خطأ أثناء إضافة البديل')
    }
  }

  // Map foreign word to component format
  const mappedForeignWord = foreignWord ? {
    id: foreignWord.id,
    word: foreignWord.word,
    wordArabic: foreignWord.word_arabic,
    categoryId: foreignWord.category_id || '',
    categoryName: foreignWord.category?.name || '',
    categoryNameEnglish: foreignWord.category?.name_english || '',
    submittedBy: foreignWord.submitted_by || '',
    submittedAt: foreignWord.submitted_at,
    context: foreignWord.context,
    reasoning: foreignWord.reasoning,
  } : null

  // Map alternatives to component format
  const mappedAlternatives: Alternative[] = foreignWord?.alternatives?.map((alt: any) => ({
    id: alt.id,
    foreignWordId: foreignWord.id,
    egyptianWord: alt.egyptian_word,
    explanation: alt.explanation,
    proposedBy: alt.proposer?.id || '',
    proposedAt: alt.proposed_at,
    upvotes: alt.upvotes || 0,
    downvotes: alt.downvotes || 0,
    netScore: alt.net_score || 0,
  })) || []

  // Map users to component format
  const mappedUsers = users.map(u => ({
    id: u.id,
    name: u.name,
    avatarUrl: u.avatar_url,
  }))

  // Map current user to component format
  const currentUser = user ? {
    id: user.id,
    name: user.user_metadata?.name || user.email?.split('@')[0] || 'User',
    avatarUrl: user.user_metadata?.avatar_url,
  } : undefined

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-slate-900">
        <div className="text-slate-400">جاري التحميل...</div>
      </div>
    )
  }

  if (!mappedForeignWord) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-slate-900">
        <div className="text-slate-400">الكلمة غير موجودة</div>
      </div>
    )
  }

  return (
    <AlternativesVoting
      foreignWord={mappedForeignWord}
      alternatives={mappedAlternatives}
      users={mappedUsers}
      currentUser={currentUser || { id: '', name: 'Guest', avatarUrl: null }}
      userVotes={userVotes}
      onVote={handleVote}
      onProposeAlternative={handleProposeAlternative}
      onShare={handleShare}
    />
  )
}
